/**
 * useSpaceUserSelection
 *
 * Platform-agnostic hook for managing multi-space user selection.
 * Handles adding, removing users with deduplication and space grouping.
 *
 * Usage:
 * - Web: Used with SpaceUserSelector modal
 * - Mobile: Used with user selection screens
 */

import { useState, useCallback, useMemo } from "react";
import { SelectedUser, SpaceUserPayload } from "./transmittal.types";
import { objectIdString } from "@Helpers/schemas/common.schema";

interface UseSpaceUserSelectionParams {
  initialUsers?: SelectedUser[];
}

interface UseSpaceUserSelectionReturn {
  // State
  selectedUsers: SelectedUser[];

  // Derived
  usersBySpace: Record<string, SelectedUser[]>;
  selectedUserIds: Set<string>;
  hasUsers: boolean;
  userCount: number;

  // Actions
  addUsers: (users: SelectedUser[]) => void;
  removeUser: (compositeId: string) => void;
  setUsers: (users: SelectedUser[]) => void;
  clear: () => void;

  // Transformers (for API payload)
  toSpaceUserPayload: () => SpaceUserPayload[];

  // Utilities
  isUserSelected: (compositeId: string) => boolean;
  getUsersForSpace: (spaceId: string) => SelectedUser[];
}

function useSpaceUserSelection({ initialUsers = [] }: UseSpaceUserSelectionParams = {}): UseSpaceUserSelectionReturn {
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>(initialUsers);

  // Derived: Group users by space
  const usersBySpace = useMemo<Record<string, SelectedUser[]>>(() => {
    return selectedUsers.reduce(
      (groups, user) => {
        if (!groups[user.spaceId]) {
          groups[user.spaceId] = [];
        }
        groups[user.spaceId]!.push(user);
        return groups;
      },
      {} as Record<string, SelectedUser[]>,
    );
  }, [selectedUsers]);

  // Derived: Set of selected user IDs (composite keys)
  const selectedUserIds = useMemo<Set<string>>(() => {
    return new Set(selectedUsers.map((user) => user.id));
  }, [selectedUsers]);

  // Derived: boolean checks
  const hasUsers = useMemo(() => selectedUsers.length > 0, [selectedUsers.length]);
  const userCount = useMemo(() => selectedUsers.length, [selectedUsers.length]);

  // Action: Add users (with deduplication)
  const addUsers = useCallback((newUsers: SelectedUser[]) => {
    setSelectedUsers((prev) => {
      const existingIds = new Set(prev.map((u) => u.id));
      const usersToAdd = newUsers.filter((u) => !existingIds.has(u.id));
      return [...prev, ...usersToAdd];
    });
  }, []);

  // Action: Remove user by composite ID
  const removeUser = useCallback((compositeId: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== compositeId));
  }, []);

  // Action: Set users directly
  const setUsers = useCallback((users: SelectedUser[]) => {
    setSelectedUsers(users);
  }, []);

  // Action: Clear all users
  const clear = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  // Transformer: Convert to API payload format
  // Output: [{ spaceId, userId: [id1, id2] }, ...]
  const toSpaceUserPayload = useCallback((): SpaceUserPayload[] => {
    const result: Record<string, SpaceUserPayload> = {};

    selectedUsers.forEach((user) => {
      // Entry point: `SelectedUser.id` is the platform-specific picker's composite
      // `${userId}_${spaceId}` key and `SelectedUser.spaceId` a plain string (web SpaceUserSelector
      // and the mobile picker both build them by hand), so neither is branded by construction.
      // `SpaceUserPayload` wants real ObjectIds, so the halves are validated here; a row whose ids
      // are not ObjectIds names nothing the save route could resolve and is dropped.
      const userId = objectIdString.safeParse(user.id.split("_")[0]).data;
      const spaceId = objectIdString.safeParse(user.spaceId).data;
      if (!userId || !spaceId) return;

      if (!result[spaceId]) {
        result[spaceId] = {
          spaceId,
          userId: [],
        };
      }

      if (!result[spaceId]!.userId.includes(userId)) {
        result[spaceId]!.userId.push(userId);
      }
    });

    return Object.values(result);
  }, [selectedUsers]);

  // Utility: Check if user is selected
  const isUserSelected = useCallback(
    (compositeId: string): boolean => {
      return selectedUserIds.has(compositeId);
    },
    [selectedUserIds],
  );

  // Utility: Get users for a specific space
  const getUsersForSpace = useCallback(
    (spaceId: string): SelectedUser[] => {
      return usersBySpace[spaceId] || [];
    },
    [usersBySpace],
  );

  return {
    // State
    selectedUsers,

    // Derived
    usersBySpace,
    selectedUserIds,
    hasUsers,
    userCount,

    // Actions
    addUsers,
    removeUser,
    setUsers,
    clear,

    // Transformers
    toSpaceUserPayload,

    // Utilities
    isUserSelected,
    getUsersForSpace,
  };
}

export default useSpaceUserSelection;
